import { FocusHeading } from "@kovernow/react-ui/a11y/focus-heading";
import { Button } from "@kovernow/react-ui/components/button";
import { Wrench } from "lucide-react";
import { useEffect, useState } from "react";
import { useServices } from "../core/app-context";
import { useT } from "../core/use-t";

/**
 * GET /serverStatus 503 (CAP-4/Maintenance): full screen, shell hidden, the
 * h1 takes focus. "Try again" re-checks and, when up, continues to the
 * requested URL (the caller keeps it). A 406 forced-update is ignored on web.
 */
export function MaintenanceView({ onRecovered }: { onRecovered: () => void }) {
  const { authApi, format } = useServices();
  const t = useT();
  const [checkedAt, setCheckedAt] = useState(() => Date.now());
  const [checks, setChecks] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.title = t("common.pageTitle", { page: t("maintenance.title") });
  }, [t]);

  const retry = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const status = await authApi.serverStatus();
      if (status === "ok") {
        onRecovered();
        return;
      }
    } catch {
      // Still unreachable: stay on this screen.
    }
    setCheckedAt(Date.now());
    setChecks((n) => n + 1);
    setBusy(false);
  };

  return (
    <main id="main" className="mx-auto flex min-h-dvh w-full max-w-xl flex-col justify-center gap-4 px-4 py-10">
      <Wrench aria-hidden="true" className="size-10 text-primary" />
      <FocusHeading focusKey={checks}>{t("maintenance.title")}</FocusHeading>
      <p>{t("maintenance.body")}</p>
      <p className="text-sm text-muted-foreground" aria-live="polite">
        {t("maintenance.lastChecked", { time: format.time(checkedAt) })}
      </p>
      <Button
        type="button"
        variant="cta"
        size="touch"
        className="self-start"
        aria-disabled={busy ? true : undefined}
        onClick={() => void retry()}
      >
        {busy ? t("common.loading") : t("maintenance.retry")}
      </Button>
    </main>
  );
}
